import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../supabase.js';
import { PageHeader } from '../ui/index.js';

interface Settings {
  tin: string;
  username: string;
  password: string;
}

const BLANK: Settings = { tin: '', username: '', password: '' };

function generatePassword(len = 12): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  return Array.from(crypto.getRandomValues(new Uint8Array(len)))
    .map((b) => chars[b % chars.length])
    .join('');
}

export function SettingsView({ clientId }: { clientId: string | null }) {
  const { t } = useTranslation();
  const [form, setForm] = useState<Settings>(BLANK);
  const [lastToken, setLastToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);

  // POS password state
  const [posPassword, setPosPassword] = useState('');
  const [posPasswordLoading, setPosPasswordLoading] = useState(true);
  const [posPasswordSaving, setPosPasswordSaving] = useState(false);
  const [showPosPass, setShowPosPass] = useState(false);
  const [posPasswordMsg, setPosPasswordMsg] = useState<{ text: string; ok: boolean } | null>(null);

  useEffect(() => {
    if (!supabase || !clientId) {
      setLoading(false);
      setPosPasswordLoading(false);
      return;
    }
    supabase
      .from('client_tax_settings')
      .select('tin, username, password, last_token')
      .eq('client_id', clientId)
      .maybeSingle()
      .then(({ data }) => {
        const row = data as {
          tin: string;
          username: string;
          password: string;
          last_token?: string | null;
        } | null;
        if (row) {
          setForm({ tin: row.tin, username: row.username, password: row.password });
          setLastToken(row.last_token ?? null);
        }
        setLoading(false);
      });

    supabase
      .from('clients')
      .select('pos_password')
      .eq('id', clientId)
      .maybeSingle()
      .then(({ data }) => {
        setPosPassword((data as { pos_password: string | null } | null)?.pos_password ?? '');
        setPosPasswordLoading(false);
      });
  }, [clientId]);

  async function save() {
    if (!supabase || !clientId) return;
    setSaving(true);
    setMsg(null);
    const { error } = await supabase
      .from('client_tax_settings')
      .upsert(
        { client_id: clientId, ...form, updated_at: new Date().toISOString() },
        { onConflict: 'client_id' },
      );
    setSaving(false);
    if (error) {
      setMsg({ text: error.message, ok: false });
    } else {
      setMsg({ text: t('settings.saved'), ok: true });
      setShowPass(false);
    }
  }

  async function savePosPassword() {
    if (!supabase || !clientId) return;
    setPosPasswordSaving(true);
    setPosPasswordMsg(null);
    const { error } = await supabase
      .from('clients')
      .update({ pos_password: posPassword || null })
      .eq('id', clientId);
    setPosPasswordSaving(false);
    if (error) {
      setPosPasswordMsg({ text: error.message, ok: false });
    } else {
      setPosPasswordMsg({ text: t('settings.saved'), ok: true });
      setShowPosPass(false);
    }
  }

  return (
    <section className="ui-stack">
      <PageHeader title={t('settings.title')} />

      <div className="panel" style={{ maxWidth: 520 }}>
        <div className="panel-header">
          <span className="panel-title">{t('settings.taxTitle')}</span>
        </div>

        <div style={{ padding: '16px 16px 4px' }}>
          <p style={{ margin: '0 0 16px' }}>{t('settings.taxIntro')}</p>
        </div>

        {loading ? (
          <div className="empty-state">{t('common.loading')}</div>
        ) : (
          <div
            style={{ padding: '0 16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}
          >
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
              <span style={{ fontWeight: 600 }}>{t('settings.tin')}</span>
              <input
                type="text"
                value={form.tin}
                onChange={(e) => setForm((f) => ({ ...f, tin: e.target.value }))}
                placeholder={t('settings.tinPlaceholder')}
                autoComplete="off"
                style={{ maxWidth: 240 }}
              />
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
              <span style={{ fontWeight: 600 }}>{t('settings.username')}</span>
              <input
                type="text"
                value={form.username}
                onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
                placeholder={t('settings.usernamePlaceholder')}
                autoComplete="off"
                style={{ maxWidth: 240 }}
              />
            </label>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
              <span style={{ fontWeight: 600 }}>{t('settings.password')}</span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', maxWidth: 320 }}>
                <input
                  type={showPass ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  autoComplete="new-password"
                  style={{ flex: 1 }}
                />
                <button
                  className="btn-ghost btn-sm"
                  type="button"
                  onClick={() => setShowPass((v) => !v)}
                >
                  {showPass ? t('common.hide') : t('common.show')}
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 4 }}>
              <button className="btn" onClick={save} disabled={saving}>
                {saving ? t('common.saving') : t('common.save')}
              </button>
              {msg && (
                <span
                  style={{
                    fontSize: 13,
                    color: msg.ok ? 'var(--success)' : 'var(--danger)',
                  }}
                >
                  {msg.text}
                </span>
              )}
            </div>

            {lastToken && (
              <div
                style={{
                  marginTop: 16,
                  paddingTop: 16,
                  borderTop: '1px solid var(--border)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                  fontSize: 13,
                }}
              >
                <span style={{ fontWeight: 600, color: 'var(--text-muted)' }}>
                  {t('settings.activeToken')}
                </span>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <code
                    style={{
                      fontSize: 11,
                      wordBreak: 'break-all',
                      background: 'var(--bg-alt, var(--bg))',
                      padding: '4px 8px',
                      borderRadius: 4,
                      flex: 1,
                      filter: showToken ? 'none' : 'blur(4px)',
                      userSelect: showToken ? 'text' : 'none',
                      transition: 'filter 0.15s',
                    }}
                  >
                    {lastToken}
                  </code>
                  <button
                    className="btn-ghost btn-sm"
                    type="button"
                    onClick={() => setShowToken((v) => !v)}
                  >
                    {showToken ? t('common.hide') : t('common.show')}
                  </button>
                </div>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {t('settings.tokenHint')}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── POS Device Password ─────────────────────────────────────────── */}
      <div className="panel" style={{ maxWidth: 520, marginTop: 24 }}>
        <div className="panel-header">
          <span className="panel-title">{t('settings.posTitle')}</span>
        </div>
        <div style={{ padding: '16px 16px 4px' }}>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--text-muted)' }}>
            {t('settings.posIntro')}
          </p>
        </div>
        {posPasswordLoading ? (
          <div className="empty-state">{t('common.loading')}</div>
        ) : (
          <div
            style={{ padding: '0 16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
              <span style={{ fontWeight: 600 }}>{t('settings.posPassword')}</span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', maxWidth: 360 }}>
                <input
                  type={showPosPass ? 'text' : 'password'}
                  value={posPassword}
                  onChange={(e) => setPosPassword(e.target.value)}
                  placeholder={t('settings.posPlaceholder')}
                  autoComplete="new-password"
                  style={{ flex: 1, fontFamily: 'monospace' }}
                />
                <button
                  className="btn-ghost btn-sm"
                  type="button"
                  onClick={() => setShowPosPass((v) => !v)}
                >
                  {showPosPass ? t('common.hide') : t('common.show')}
                </button>
                <button
                  className="btn-ghost btn-sm"
                  type="button"
                  onClick={() => {
                    setPosPassword(generatePassword());
                    setShowPosPass(true);
                  }}
                >
                  {t('settings.generate')}
                </button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 4 }}>
              <button className="btn" onClick={savePosPassword} disabled={posPasswordSaving}>
                {posPasswordSaving ? t('common.saving') : t('common.save')}
              </button>
              {posPasswordMsg && (
                <span
                  style={{
                    fontSize: 13,
                    color: posPasswordMsg.ok ? 'var(--success)' : 'var(--danger)',
                  }}
                >
                  {posPasswordMsg.text}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
